/*
 * newsbot - Blocklist settings page
 */

import { Fragment, useRef, useState } from "react";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import {
  EllipsisHorizontalIcon,
  TrashIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";

import { useToggle } from "@hooks/hooks";
import { classNames } from "@utils";
import { toast } from "@components/hot-toast";
import Toast from "@components/notifications/Toast";
import { DeleteModal } from "@components/modals";
import { EmptySimple } from "@components/emptystates";
import { Checkbox } from "@components/Checkbox";
import { Section } from "./_components";
import {
  useBlocklistRules,
  useCreateBlocklistRule,
  useUpdateBlocklistRule,
  useDeleteBlocklistRule,
} from "@api/queries";
import { BlocklistRule, BlocklistRuleType } from "@/types";

function Blocklist() {
  const { data: rules, isLoading } = useBlocklistRules();
  const [showAddForm, setShowAddForm] = useState(false);

  if (isLoading) {
    return (
      <Section title="Blocklist" description="Manage blocklist rules to filter content.">
        <div className="animate-pulse">Loading...</div>
      </Section>
    );
  }

  return (
    <Section title="Blocklist" description="Manage blocklist rules to filter content.">
      <div className="mb-4">
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Rule
        </button>
      </div>

      {showAddForm && <AddRuleForm onClose={() => setShowAddForm(false)} />}

      {rules && rules.length > 0 ? (
        <ul className="min-w-full relative">
          <li className="grid grid-cols-12 border-b border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">
            <div className="flex col-span-1 pl-4 py-3">Enabled</div>
            <div className="col-span-2 py-3">Type</div>
            <div className="col-span-4 py-3">Pattern</div>
            <div className="hidden md:flex col-span-3 py-3">Reason</div>
            <div className="col-span-1 py-3">Hits</div>
            <div className="col-span-1 py-3"></div>
          </li>
          {rules.map((rule) => (
            <ListItem key={rule.id} rule={rule} />
          ))}
        </ul>
      ) : (
        <EmptySimple
          title="No blocklist rules"
          subtitle="Add a rule to start filtering content"
        />
      )}
    </Section>
  );
}

interface AddRuleFormProps {
  onClose: () => void;
}

function AddRuleForm({ onClose }: AddRuleFormProps) {
  const [ruleType, setRuleType] = useState<BlocklistRuleType>("domain");
  const [pattern, setPattern] = useState("");
  const [reason, setReason] = useState("");
  const createMutation = useCreateBlocklistRule();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!pattern.trim()) {
      toast.custom((t) => (
        <Toast type="error" body="Pattern is required" t={t} />
      ));
      return;
    }

    createMutation.mutate(
      {
        rule_type: ruleType,
        pattern: pattern.trim(),
        reason: reason.trim() || undefined,
        enabled: true,
      },
      {
        onSuccess: () => {
          toast.custom((t) => (
            <Toast type="success" body="Blocklist rule created successfully" t={t} />
          ));
          setPattern("");
          setReason("");
          onClose();
        },
        onError: (error) => {
          toast.custom((t) => (
            <Toast
              type="error"
              body={`Failed to create rule: ${error.message}`}
              t={t}
            />
          ));
        },
      }
    );
  };

  return (
    <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
        Add New Blocklist Rule
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="rule-type"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Rule Type
          </label>
          <select
            id="rule-type"
            value={ruleType}
            onChange={(e) => setRuleType(e.target.value as BlocklistRuleType)}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
          >
            <option value="domain">Domain</option>
            <option value="keyword">Keyword</option>
            <option value="url_pattern">URL Pattern</option>
          </select>
        </div>

        <div>
          <label
            htmlFor="pattern"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Pattern
          </label>
          <input
            type="text"
            id="pattern"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder={
              ruleType === "domain"
                ? "example.com"
                : ruleType === "keyword"
                ? "spam"
                : "*/ads/*"
            }
            className="mt-1 block w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md shadow-sm py-2 px-3 focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        <div>
          <label
            htmlFor="reason"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Reason (optional)
          </label>
          <input
            type="text"
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this blocked?"
            className="mt-1 block w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md shadow-sm py-2 px-3 focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {createMutation.isPending ? "Creating..." : "Create Rule"}
          </button>
        </div>
      </form>
    </div>
  );
}

interface ListItemProps {
  rule: BlocklistRule;
}

function ListItem({ rule }: ListItemProps) {
  const [enabled, setEnabled] = useState(rule.enabled);
  const updateMutation = useUpdateBlocklistRule();

  const toggleEnabled = (status: boolean) => {
    setEnabled(status);
    updateMutation.mutate(
      { id: rule.id, enabled: status },
      {
        onSuccess: () => {
          toast.custom((t) => (
            <Toast
              type="success"
              body={`Rule was ${status ? "enabled" : "disabled"} successfully.`}
              t={t}
            />
          ));
        },
      }
    );
  };

  const getRuleTypeBadge = (type: BlocklistRuleType) => {
    const colors = {
      domain: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
      keyword: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
      url_pattern: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
    };

    const labels = {
      domain: "Domain",
      keyword: "Keyword",
      url_pattern: "URL Pattern",
    };

    return (
      <span
        className={classNames(
          "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
          colors[type]
        )}
      >
        {labels[type]}
      </span>
    );
  };

  return (
    <li key={rule.id}>
      <div className="grid grid-cols-12 items-center text-sm font-medium text-gray-900 dark:text-gray-500">
        <div className="col-span-1 pl-6 flex items-center">
          <Checkbox value={enabled} setValue={toggleEnabled} ariaLabel={`${rule.pattern} enabled`} />
        </div>
        <div className="col-span-2 py-3 flex items-center">
          {getRuleTypeBadge(rule.rule_type)}
        </div>
        <div className="col-span-4 py-3 flex flex-col">
          <span className="pr-2 dark:text-white truncate">{rule.pattern}</span>
        </div>
        <div className="hidden md:flex col-span-3 py-3 items-center">
          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {rule.reason || "-"}
          </span>
        </div>
        <div className="col-span-1 py-3 flex items-center">
          <span className="text-sm text-gray-900 dark:text-gray-300">{rule.hits}</span>
        </div>
        <div className="col-span-1 flex justify-center items-center">
          <RuleItemDropdown rule={rule} />
        </div>
      </div>
    </li>
  );
}

interface RuleItemDropdownProps {
  rule: BlocklistRule;
}

const RuleItemDropdown = ({ rule }: RuleItemDropdownProps) => {
  const cancelModalButtonRef = useRef(null);
  const [deleteModalIsOpen, toggleDeleteModal] = useToggle(false);
  const deleteMutation = useDeleteBlocklistRule();

  return (
    <Menu as="div">
      <DeleteModal
        isOpen={deleteModalIsOpen}
        isLoading={deleteMutation.isPending}
        toggle={toggleDeleteModal}
        buttonRef={cancelModalButtonRef}
        deleteAction={() => {
          deleteMutation.mutate(rule.id, {
            onSuccess: () => {
              toast.custom((t) => (
                <Toast type="success" body="Blocklist rule deleted" t={t} />
              ));
              toggleDeleteModal();
            },
          });
        }}
        title={`Remove blocklist rule: ${rule.pattern}`}
        text="Are you sure you want to remove this rule? This action cannot be undone."
      />
      <MenuButton className="px-4 py-2">
        <EllipsisHorizontalIcon
          className="w-5 h-5 text-gray-700 hover:text-gray-900 dark:text-gray-100 dark:hover:text-gray-400"
          aria-hidden="true"
        />
      </MenuButton>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems
          anchor={{ to: "bottom end", padding: "8px" }}
          className="absolute w-56 bg-white dark:bg-gray-825 divide-y divide-gray-200 dark:divide-gray-750 rounded-md shadow-lg border border-gray-250 dark:border-gray-750 focus:outline-hidden z-10"
        >
          <div className="px-1 py-1">
            <MenuItem>
              {({ active }) => (
                <button
                  className={classNames(
                    active ? "bg-red-600 text-white" : "text-gray-900 dark:text-gray-300",
                    "font-medium group flex rounded-md items-center w-full px-2 py-2 text-sm"
                  )}
                  onClick={() => toggleDeleteModal()}
                >
                  <TrashIcon
                    className={classNames(
                      active ? "text-white" : "text-red-500",
                      "w-5 h-5 mr-2"
                    )}
                    aria-hidden="true"
                  />
                  Delete
                </button>
              )}
            </MenuItem>
          </div>
        </MenuItems>
      </Transition>
    </Menu>
  );
};

export default Blocklist;
